import { useRef, useState, type ReactNode } from "react";
import { prettifyApiError } from "@/shared/api";
import { draftToOverride, getMailBackend, type MailDraft } from "@/features/mail/api";
import { MailComposeMultiDialog } from "@/features/mail/MailComposeMultiDialog";
import type { Order } from "./types";
import {
  ordersWithAllServiceSku,
  sendAllServiceMailBulk,
  type AllServiceMailBulkResult,
} from "./allserviceApi";
import {
  fetchCosmaPreview,
  ordersCosma,
  sendCosmaOrdersBulk,
  type CosmaPackage,
  type CosmaPreviewResult,
} from "./cosmaApi";
import {
  fetchWebFurniturePreview,
  NOMI_FORNITORE_MAIL,
  ordersWithFornitoreMail,
  fornitoreViaHttp,
  ordersWithWebFurnitureSku,
  sendWebFurnitureOrderBulk,
  type WebFurniturePackage,
  type WebFurniturePreviewResult,
  type WebFurnitureSendBulkResult,
} from "./webfurnitureApi";
import { titoloMailCompose } from "./titoloMailCompose";
import { ordersBizzotto, ordersItamoby, ordersItyhome } from "./portalOrdersApi";
import { usePortalOrderAction } from "./usePortalOrderAction";
import { CosmaConfirmDialog } from "./components/CosmaConfirmDialog";
import { WebFurnitureConfirmDialog } from "./components/WebFurnitureConfirmDialog";

/** Un fornitore a cui OM sa mandare l'ordine, con gli ordini che gli toccano. */
export interface FlussoFornitore {
  id: string;
  label: string;
  desc: string;
  count: number;
  run: () => void;
}

interface Opts {
  onMessage: (msg: string) => void;
  onError: (msg: string) => void;
  onRefresh: () => void;
}

export interface OrdinaFornitore {
  /** I fornitori presenti fra ``orders``, ognuno col SUO sottoinsieme. */
  flussi: (orders: Order[]) => FlussoFornitore[];
  /** Cosma con ``force``: ri-invia anche gli ordini gia' caricati. */
  runCosma: (orders: Order[], force: boolean) => void;
  /** Un'anteprima si sta caricando (spinner sul tasto che l'ha lanciata). */
  busy: boolean;
  /** Un popup e' aperto o un invio e' in volo: gate per ogni altra azione. */
  dialogBusy: boolean;
  /** I popup di conferma: li renderizza chi usa l'hook. */
  dialogs: ReactNode;
}

/**
 * «Ordina al fornitore», in un posto solo.
 *
 * Nato dentro la barra della lista (Davide 2026-08-13: un tasto unico al posto
 * di cinque), ora serve anche alla pagina ordine (Davide 2026-10-23: «voglio
 * poter fare TUTTO»). Due copie dello stesso flusso divergono alla prima
 * modifica, e qui il flusso decide quando parte una mail o una spesa vera.
 *
 * Regola anti-errore che resta intatta: ogni flusso agisce SOLO sugli ordini del
 * proprio fornitore, mai sull'intera lista che gli si passa. E nessun flusso
 * parte al clic: prima l'anteprima, poi la conferma nel popup.
 */
export function useOrdinaFornitore({ onMessage, onError, onRefresh }: Opts): OrdinaFornitore {
  const [asMailBusy, setAsMailBusy] = useState(false);
  const [cosmaBusy, setCosmaBusy] = useState(false);
  // Render condizionale {cosmaPreview && ...}: ogni apertura e' un mount
  // fresco (il dialog fotografa previews nello stato locale al mount) — mai
  // sostituire cosmaPreview in-place senza passare da null.
  const [cosmaPreview, setCosmaPreview] = useState<CosmaPreviewResult | null>(null);
  const [cosmaSending, setCosmaSending] = useState(false);
  const [cosmaSendError, setCosmaSendError] = useState<string | null>(null);
  // ``force`` scelto all'apertura dell'anteprima, letto alla conferma: un ref
  // perche' non serve un re-render, solo che sia leggibile dopo il popup.
  const cosmaForceRef = useRef(false);
  const ithAction = usePortalOrderAction({
    supplierId: "ityhome",
    supplierLabel: "ITYHOME",
    onMessage,
    onError,
    onRefresh,
  });
  const itaAction = usePortalOrderAction({
    supplierId: "itamoby",
    supplierLabel: "ITAMOBY",
    onMessage,
    onError,
    onRefresh,
  });
  // Bizzotto: dall'altra parte un browser headless riempie il carrello del
  // portale. ⚠️ La merce arriva a BS COM, non al cliente.
  const bizAction = usePortalOrderAction({
    supplierId: "bizzotto",
    supplierLabel: "BIZZOTTO",
    onMessage,
    onError,
    onRefresh,
  });
  const [wfBusy, setWfBusy] = useState(false);
  // Per QUALE fornitore-via-mail e' in corso il flusso: Web Furniture, Aurora e
  // Mauro Ferretti condividono dialog e handler. Fotografato al clic — se lo
  // rileggessimo dalla lista, cambiarla col popup aperto manderebbe la mail
  // all'altro fornitore.
  const [mailFornitore, setMailFornitore] = useState("webfurniture");
  const [wfPreview, setWfPreview] = useState<WebFurniturePreviewResult | null>(null);
  const [wfSending, setWfSending] = useState(false);
  const [wfSendError, setWfSendError] = useState<string | null>(null);
  // Popup pre-invio mail (backend smtp): ``originals`` serve a draftToOverride
  // per il delta, ``ids`` fotografa gli ordini visti nel popup — si invia
  // ESATTAMENTE cio' che l'utente ha confermato.
  const [composeState, setComposeState] = useState<{
    flow: "as" | "wf";
    drafts: MailDraft[];
    originals: MailDraft[];
    ids: string[];
    wfPackagesOverrides?: Record<string, WebFurniturePackage[]>;
    errors: { order_id: string; message: string }[];
  } | null>(null);
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);

  const dialogBusy =
    cosmaSending ||
    cosmaPreview !== null ||
    wfSending ||
    wfPreview !== null ||
    composeSending ||
    composeState !== null ||
    ithAction.dialogBusy ||
    itaAction.dialogBusy ||
    bizAction.dialogBusy;
  const busy =
    asMailBusy || wfBusy || cosmaBusy || ithAction.busy || itaAction.busy || bizAction.busy;
  const fermo = busy || dialogBusy;

  function reportAsMailSent(result: AllServiceMailBulkResult) {
    const refs = result.sent.map((s) => s.reference).join(", ");
    const errCount = result.errors.length;
    const base = `Mail AS inviata a ${result.recipient} con ${result.sent.length} allegati (${refs}).`;
    onMessage(errCount ? `${base} ${errCount} ordini ignorati.` : base);
    onRefresh();
  }

  /** Una mail ad AllService con N allegati. Con smtp NON invia: chiede
   *  l'anteprima e apre il popup; l'invio parte da ``handleComposeSend``. */
  async function runMailAs(ordini: Order[]) {
    if (fermo || ordini.length === 0) return;
    setAsMailBusy(true);
    onMessage("");
    onError("");
    try {
      const ids = ordini.map((o) => o.id);
      const backend = await getMailBackend().catch(() => "thunderbird" as const);
      if (backend === "smtp") {
        const preview = await sendAllServiceMailBulk(ids, { preview: true });
        if ("preview" in preview) {
          setComposeError(null);
          setComposeState({
            flow: "as",
            drafts: preview.drafts,
            originals: preview.drafts.map((d) => ({ ...d })),
            ids,
            errors: preview.errors ?? [],
          });
          return;
        }
        // Difensivo: il backend ha ignorato preview:true e ha gia' inviato.
        reportAsMailSent(preview);
        return;
      }
      reportAsMailSent(await sendAllServiceMailBulk(ids));
    } catch (err) {
      onError(prettifyApiError(err));
    } finally {
      setAsMailBusy(false);
    }
  }

  function reportWfMailSent(result: WebFurnitureSendBulkResult) {
    const refs = result.sent.map((s) => s.reference).join(", ");
    const errCount = result.errors.length;
    const nome = NOMI_FORNITORE_MAIL[mailFornitore] ?? mailFornitore;
    if (result.sent.length === 0) {
      const detail = result.errors.map((e) => e.message).join("; ");
      onError(detail || `Nessun ordine ${nome} inviato`);
    } else {
      const base = `Ordine ${nome} inviato a ${result.recipient}: ${result.sent.length} ordini (${refs}).`;
      const errSuffix = errCount ? ` ${errCount} ordini con errore.` : "";
      // Avvisi non bloccanti (ordine gia' spedito, colli non applicati):
      // appesi al successo, per un feedback onesto.
      const warnSuffix = result.warnings.length > 0 ? ` ⚠ ${result.warnings.join(" · ")}` : "";
      onMessage(`${base}${errSuffix}${warnSuffix}`);
    }
    onRefresh();
  }

  async function handleComposeSend(edited: MailDraft[]) {
    if (!composeState) return;
    setComposeSending(true);
    setComposeError(null);
    try {
      if (composeState.flow === "as") {
        const originalDraft = composeState.originals[0];
        const editedDraft = edited[0];
        if (!originalDraft || !editedDraft) return;
        reportAsMailSent(
          await sendAllServiceMailBulk(composeState.ids, {
            mailOverride: draftToOverride(editedDraft, originalDraft),
          }),
        );
      } else {
        const originalsByKey = new Map(composeState.originals.map((d) => [d.draft_key, d]));
        const mailOverrides = Object.fromEntries(
          edited.map((d) => {
            const original = originalsByKey.get(d.draft_key) ?? d;
            return [d.draft_key, draftToOverride(d, original)];
          }),
        );
        reportWfMailSent(
          await sendWebFurnitureOrderBulk(composeState.ids, composeState.wfPackagesOverrides, {
            mailOverrides,
            fornitore: mailFornitore,
          }),
        );
      }
      setComposeState(null);
    } catch (err) {
      setComposeError(prettifyApiError(err));
    } finally {
      setComposeSending(false);
    }
  }

  /** «Ordina a Web Furniture / Aurora / Mauro Ferretti»: carica l'anteprima
   *  colli e apre il popup. ``ordini`` e' GIA' il sottoinsieme del fornitore. */
  async function runOrdineMail(fornitore: string, ordini: readonly { id: string }[]) {
    if (fermo || ordini.length === 0) return;
    setMailFornitore(fornitore);
    setWfBusy(true);
    onMessage("");
    onError("");
    try {
      const preview = await fetchWebFurniturePreview(
        ordini.map((o) => o.id),
        fornitore,
      );
      setWfSendError(null);
      setWfPreview(preview);
    } catch (err) {
      onError(prettifyApiError(err));
    } finally {
      setWfBusy(false);
    }
  }

  /** Conferma dal popup colli. Con smtp chiede l'anteprima della mail PASSANDO
   *  i colli appena confermati e apre il popup mail; errore → resta nel popup
   *  colli per non perdere le modifiche. */
  async function confirmWebFurniture(overrides: Record<string, WebFurniturePackage[]>) {
    if (!wfPreview) return;
    setWfSending(true);
    setWfSendError(null);
    try {
      const refs = wfPreview.orders.map((o) => o.reference);
      const backend = await getMailBackend().catch(() => "thunderbird" as const);
      // Chi si ordina via portale salta il popup di composizione mail.
      if (backend === "smtp" && !fornitoreViaHttp(mailFornitore)) {
        const preview = await sendWebFurnitureOrderBulk(refs, overrides, {
          preview: true,
          fornitore: mailFornitore,
        });
        if ("preview" in preview) {
          setComposeError(null);
          setComposeState({
            flow: "wf",
            drafts: preview.drafts,
            originals: preview.drafts.map((d) => ({ ...d })),
            ids: refs,
            wfPackagesOverrides: overrides,
            errors: preview.errors ?? [],
          });
          setWfPreview(null);
          return;
        }
        reportWfMailSent(preview);
        setWfPreview(null);
        return;
      }
      reportWfMailSent(
        await sendWebFurnitureOrderBulk(refs, overrides, { fornitore: mailFornitore }),
      );
      setWfPreview(null);
    } catch (err) {
      setWfSendError(prettifyApiError(err));
    } finally {
      setWfSending(false);
    }
  }

  /** Cosma: anteprima (solo DB) e popup. ``force`` bypassa la dedup sent-log. */
  async function runCosmaPreview(ordini: Order[], force: boolean) {
    const cosma = ordersCosma(ordini);
    if (fermo || cosma.length === 0) return;
    cosmaForceRef.current = force;
    setCosmaBusy(true);
    onMessage("");
    onError("");
    try {
      const preview = await fetchCosmaPreview(cosma.map((o) => o.id));
      setCosmaSendError(null);
      setCosmaPreview(preview);
    } catch (err) {
      onError(prettifyApiError(err));
    } finally {
      setCosmaBusy(false);
    }
  }

  async function confirmCosma(overrides: Record<string, CosmaPackage[]>) {
    if (!cosmaPreview) return;
    setCosmaSending(true);
    setCosmaSendError(null);
    try {
      const result = await sendCosmaOrdersBulk(
        // Id fotografati dall'anteprima: si invia ESATTAMENTE cio' che si e'
        // visto e confermato, anche se la lista sotto e' cambiata.
        cosmaPreview.orders.map((o) => o.reference),
        cosmaForceRef.current,
        overrides,
      );
      const ok = result.uploaded.length;
      const sk = result.skipped.length;
      const er = result.errors.length;
      const parts = [
        `Cosma: ${ok} ordin${ok === 1 ? "e" : "i"} inviat${ok === 1 ? "o" : "i"} ` +
          `(LDV in /Ldv/ + CSV in /Ordini/, verificati su FTP)`,
      ];
      if (sk) parts.push(`${sk} già inviati`);
      if (er) parts.push(`${er} errori`);
      let msg = `${parts.join(", ")}.`;
      // Il PRIMO errore reale: senza, «0 inviati» sembrava «non succede nulla».
      if (er > 0 && result.errors[0]) msg += ` — ${result.errors[0].message}`;
      if (ok === 0 && (er > 0 || sk === 0)) onError(msg);
      else onMessage(msg);
      setCosmaPreview(null);
      onRefresh();
    } catch (err) {
      setCosmaSendError(prettifyApiError(err));
    } finally {
      setCosmaSending(false);
    }
  }

  function flussi(orders: Order[]): FlussoFornitore[] {
    const as = ordersWithAllServiceSku(orders);
    const wf = ordersWithWebFurnitureSku(orders);
    // Aurora e Mauro Ferretti: il gate arriva dal PIM (``fornitoreMail``).
    const aurora = ordersWithFornitoreMail(orders, "aurora");
    const mauro = ordersWithFornitoreMail(orders, "mauroferretti");
    const cosma = ordersCosma(orders);
    const ith = ordersItyhome(orders);
    const ita = ordersItamoby(orders);
    const biz = ordersBizzotto(orders);
    return [
      {
        id: "as",
        label: "AllService",
        desc: "Una mail ad AllService con gli xlsx allegati",
        count: as.length,
        run: () => void runMailAs(as),
      },
      {
        id: "wf",
        label: "Web Furniture",
        desc: "Anteprima colli, poi mail a Web Furniture con la LDV",
        count: wf.length,
        run: () => void runOrdineMail("webfurniture", wf),
      },
      {
        id: "aurora",
        label: "Aurora Bedding",
        desc: "Anteprima colli, poi mail a Aurora con la LDV",
        count: aurora.length,
        run: () => void runOrdineMail("aurora", aurora),
      },
      {
        id: "mauroferretti",
        label: "Mauro Ferretti",
        desc: "Anteprima colli, poi ordine sul portale con la LDV",
        count: mauro.length,
        run: () => void runOrdineMail("mauroferretti", mauro),
      },
      {
        id: "cosma",
        label: "Cosma",
        desc: "Anteprima, poi CSV + LDV su FTP Cosma",
        count: cosma.length,
        run: () => void runCosmaPreview(cosma, false),
      },
      {
        id: "ityhome",
        label: "Ityhome",
        desc: "Prezzi e disponibilità dal portale, poi ordine vero",
        count: ith.length,
        run: () => void ithAction.start(ith.map((o) => o.id)),
      },
      {
        id: "itamoby",
        label: "Itamoby",
        desc: "Prezzi e disponibilità dal portale, poi ordine vero",
        count: ita.length,
        run: () => void itaAction.start(ita.map((o) => o.id)),
      },
      {
        id: "bizzotto",
        label: "Bizzotto",
        desc: "Prezzi dal portale, poi ordine vero (merce a BS COM)",
        count: biz.length,
        run: () => void bizAction.start(biz.map((o) => o.id)),
      },
    ].filter((f) => f.count > 0);
  }

  const dialogs = (
    <>
      {cosmaPreview && (
        <CosmaConfirmDialog
          previews={cosmaPreview.orders}
          previewErrors={cosmaPreview.errors}
          sending={cosmaSending}
          sendError={cosmaSendError}
          onConfirm={(overrides) => void confirmCosma(overrides)}
          onClose={() => {
            if (!cosmaSending) setCosmaPreview(null);
          }}
        />
      )}
      {wfPreview && (
        <WebFurnitureConfirmDialog
          fornitore={mailFornitore}
          previews={wfPreview.orders}
          previewErrors={wfPreview.errors}
          sending={wfSending}
          sendError={wfSendError}
          onConfirm={(overrides) => void confirmWebFurniture(overrides)}
          onClose={() => {
            if (!wfSending) setWfPreview(null);
          }}
        />
      )}
      {ithAction.dialog}
      {itaAction.dialog}
      {bizAction.dialog}
      {composeState && (
        <MailComposeMultiDialog
          drafts={composeState.drafts}
          title={titoloMailCompose(composeState.flow, mailFornitore)}
          onSendAll={(edited) => handleComposeSend(edited)}
          onClose={() => {
            if (!composeSending) setComposeState(null);
          }}
          sending={composeSending}
          sendError={composeError}
          previewErrors={composeState.errors}
        />
      )}
    </>
  );

  return {
    flussi,
    runCosma: (orders, force) => void runCosmaPreview(orders, force),
    busy,
    dialogBusy,
    dialogs,
  };
}
